import { useEffect, useState } from "react";

import type { Routine } from "./routine.types";

import "./RoutineEditor.css";

type Props = {
  open: boolean;
  routine?: Routine | null;
  onClose: () => void;
  onSave: (routine: Routine) => void;
};

const repeatOptions = [
  "Does not repeat",
  "Every day",
  "Every weekday",
  "Every week",
  "Every month",
];

function toInputDateTime(value?: string): string {
  if (!value) {
    const date = new Date();

    date.setHours(
      date.getHours() + 1,
      0,
      0,
      0,
    );

    return date.toISOString().slice(0, 16);
  }

  return new Date(value).toISOString().slice(0, 16);
}

export function RoutineEditor({
  open,
  routine,
  onClose,
  onSave,
}: Props) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [repeatRule, setRepeatRule] =
    useState("Every day");

  const [isRoutine, setIsRoutine] = useState(true);
  const [icon, setIcon] = useState("✓");
  const [location, setLocation] = useState("");

  const [notificationsEnabled, setNotificationsEnabled] =
    useState(true);

  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setTitle(routine?.title ?? "");
    setNote(routine?.note ?? "");
    setDueAt(toInputDateTime(routine?.dueAt));

    setRepeatRule(
      routine?.repeatRule ||
        (routine?.isRoutine
          ? "Every day"
          : "Does not repeat"),
    );

    setIsRoutine(routine?.isRoutine ?? true);
    setIcon(routine?.icon ?? "✓");
    setLocation(routine?.location ?? "");

    setNotificationsEnabled(
      routine?.notificationsEnabled ?? true,
    );

    setError("");
  }, [open, routine]);

  if (!open) {
    return null;
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!title.trim()) {
      setError("Task title is required.");
      return;
    }

    const targetDate = new Date(dueAt);

    if (Number.isNaN(targetDate.getTime())) {
      setError("Choose a valid date and time.");
      return;
    }

    const now = Date.now();

    onSave({
      id: routine?.id ?? crypto.randomUUID(),
      cloudId: routine?.cloudId,
      title: title.trim(),
      note: note.trim(),
      dueAt: targetDate.toISOString(),
      repeatRule: isRoutine
        ? repeatRule
        : "Does not repeat",
      isRoutine,
      completed: routine?.completed ?? false,
      icon: icon.trim() || "✓",
      location: location.trim(),
      notificationsEnabled,
      createdAt: routine?.createdAt ?? now,
      updatedAt: now,
    });
  }

  return (
    <div
      className="routine-editor__backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="routine-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="routine-editor-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="routine-editor__header">
          <div>
            <span>
              {routine
                ? "EDIT TASK"
                : "NEW TASK"}
            </span>

            <h2 id="routine-editor-title">
              {routine
                ? "Edit routine"
                : "Add routine"}
            </h2>
          </div>

          <button
            type="button"
            aria-label="Close editor"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <form onSubmit={submit}>
          <div className="routine-editor__type-row">
            <button
              className={
                isRoutine
                  ? "routine-editor__type routine-editor__type--selected"
                  : "routine-editor__type"
              }
              type="button"
              onClick={() => setIsRoutine(true)}
            >
              <strong>Routine</strong>
              <small>Repeats as part of your flow</small>
            </button>

            <button
              className={
                !isRoutine
                  ? "routine-editor__type routine-editor__type--selected"
                  : "routine-editor__type"
              }
              type="button"
              onClick={() => setIsRoutine(false)}
            >
              <strong>Reminder</strong>
              <small>One important task or moment</small>
            </button>
          </div>

          <label className="routine-editor__field">
            <span>Title</span>

            <input
              autoFocus
              maxLength={80}
              placeholder="Example: Morning planning"
              value={title}
              onChange={(event) =>
                setTitle(event.target.value)
              }
            />
          </label>

          <label className="routine-editor__field">
            <span>Note</span>

            <textarea
              maxLength={500}
              placeholder="Optional details"
              rows={3}
              value={note}
              onChange={(event) =>
                setNote(event.target.value)
              }
            />
          </label>

          <div className="routine-editor__two-fields">
            <label className="routine-editor__field">
              <span>Icon</span>

              <input
                maxLength={8}
                placeholder="✓"
                value={icon}
                onChange={(event) =>
                  setIcon(event.target.value)
                }
              />
            </label>

            <label className="routine-editor__field">
              <span>Date and time</span>

              <input
                type="datetime-local"
                value={dueAt}
                onChange={(event) =>
                  setDueAt(event.target.value)
                }
              />
            </label>
          </div>

          {isRoutine && (
            <label className="routine-editor__field">
              <span>Repeat</span>

              <select
                value={repeatRule}
                onChange={(event) =>
                  setRepeatRule(event.target.value)
                }
              >
                {repeatOptions.map((item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="routine-editor__field">
            <span>Location</span>

            <input
              maxLength={160}
              placeholder="Optional place or address"
              value={location}
              onChange={(event) =>
                setLocation(event.target.value)
              }
            />
          </label>

          <label className="routine-editor__switch">
            <input
              type="checkbox"
              checked={notificationsEnabled}
              onChange={(event) =>
                setNotificationsEnabled(
                  event.target.checked,
                )
              }
            />

            <span>
              <strong>Notifications</strong>

              <small>
                Remind me at the saved date and time.
              </small>
            </span>
          </label>

          {error && (
            <p className="routine-editor__error">
              {error}
            </p>
          )}

          <footer className="routine-editor__actions">
            <button
              className="routine-editor__cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              className="routine-editor__save"
              type="submit"
            >
              {routine
                ? "Save changes"
                : "Add task"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}